import { Badge, Box } from "@chakra-ui/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { IFilterInvoice, TableInvoiceProps } from "./interface";
import {
  PaginatorInfo,
  Invoice,
  useListInvoicesQuery,
} from "../../generated/graphql";
import { createColumnHelper } from "@tanstack/react-table";
import Filter from "./Filter";
import update from "immutability-helper";
import DefaultTable, {
  TableImperativeHandle,
  ExtendedDefaultTableProps,
} from "../DefaultTable";
import NumberFormat from "../NumberFormat";
import TextCopy from "../TextCopy";
import moment from "moment";
import {
  getInvoiceStatusColorScheme,
  getInvoiceStatusName,
} from "../../helpers/global";
import { getThisYear } from "../DateRangePicker/data";

const columnHelper = createColumnHelper<Invoice>();

const thisYear = getThisYear();

const TableInvoice = forwardRef<
  TableImperativeHandle<Invoice>,
  ExtendedDefaultTableProps<Invoice, IFilterInvoice> & TableInvoiceProps
>(
  (
    {
      filterParams,
      onFilterSubmit,
      page,
      onPageChange,
      onDelete,
      status,
      ...props
    },
    ref
  ) => {
    useImperativeHandle(ref, () => ({
      updateRow(data) {
        setDatas((datas) => {
          const index = datas.findIndex((elm) => elm.id === data.id);
          return update(datas, {
            [index]: {
              $set: { ...datas[index], ...data },
            },
          });
        });
      },
      refresh() {
        refetch();
      },
    }));

    const [localFilterParams, setLocalFilterParams] =
      useState<IFilterInvoice>();
    const [localPage, setLocalPage] = useState<number>(1);
    const [datas, setDatas] = useState<Invoice[]>([]);

    const columns = useMemo(() => {
      return [
        columnHelper.accessor("invoiceNumber", {
          header: () => "Number",
          cell: (info) =>
            info.renderValue() && (
              <TextCopy
                textCopy={info.renderValue() as string}
                description="Kode telah disalin!"
              >
                <Box as="span">{info.renderValue()}</Box>
              </TextCopy>
            ),
        }),
        columnHelper.accessor("invoiceDate", {
          header: () => "Invoice Date",
          cell: (info) => moment(info.getValue()).format("D/MMM/YYYY"),
        }),
        columnHelper.accessor("refNo", {
          header: () => "Ref No.",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("customerName", {
          header: () => "Customer",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("customerSignatureName", {
          header: () => "Customer Sign Name",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("status", {
          header: "Status",
          cell: (info) => (
            <Badge colorScheme={getInvoiceStatusColorScheme(info.getValue())}>
              {getInvoiceStatusName(info.getValue())}
            </Badge>
          ),
        }),
        columnHelper.accessor("subTotal", {
          header: "Subtotal",
          cell: (info) => <NumberFormat prefix="$ " value={info.getValue()} />,
        }),
        columnHelper.accessor("additionalCost", {
          header: "Eddition Costa",
          cell: (info) => <NumberFormat prefix="$ " value={info.getValue()} />,
        }),
        columnHelper.accessor("total", {
          header: "NTA",
          cell: (info) => <NumberFormat prefix="$ " value={info.getValue()} />,
        }),
      ];
    }, []);

    useEffect(() => {
      if (filterParams !== undefined) setLocalFilterParams(filterParams);
    }, [filterParams]);

    useEffect(() => {
      if (page !== undefined) setLocalPage(page);
    }, [page]);

    useEffect(() => {
      setLocalFilterParams((localFilterParams) => ({
        ...localFilterParams,
        status,
      }));
    }, [status]);

    const [paginator, setPaginator] = useState<PaginatorInfo>();

    const { data, loading, refetch } = useListInvoicesQuery({
      notifyOnNetworkStatusChange: true,
      variables: {
        params: {
          status: localFilterParams?.status || undefined,
        },
      },
    });

    const [isLazyLoading, setIsLazyLoading] = useState(true);

    useEffect(() => {
      setPaginator(data?.listInvoices.paginatorInfo);
      setDatas((data?.listInvoices.data || []) as Invoice[]);
    }, [data]);

    useEffect(() => {
      setIsLazyLoading(loading);
    }, [loading, setIsLazyLoading]);

    useEffect(() => {
      refetch({
        page: localPage,
        params: {
          key: localFilterParams?.key || undefined,
          status: localFilterParams?.status || undefined,
          dateRange: localFilterParams?.dateRange
            ? {
                from: moment(localFilterParams?.dateRange[0]).format("Y-MM-DD"),
                to: moment(localFilterParams?.dateRange[1]).format("Y-MM-DD"),
              }
            : {
                from: moment(thisYear[0]).format("Y-MM-DD"),
                to: moment(thisYear[1]).format("Y-MM-DD"),
              },
        },
      });
    }, [localFilterParams, localPage, refetch]);

    return (
      <DefaultTable
        data={datas}
        columns={columns}
        isLoading={loading || isLazyLoading}
        getRowId={(row) => row.id}
        filterComponent={
          <Filter
            value={localFilterParams}
            onSubmit={(data) => {
              setLocalPage(1);
              setLocalFilterParams(data);
              onFilterSubmit && onFilterSubmit(data);
            }}
          />
        }
        paginator={paginator}
        onPageChange={(page) => {
          setLocalPage(page);
          onPageChange && onPageChange(page);
        }}
        {...props}
      />
    );
  }
);

export default TableInvoice;
